import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useConsignorId } from '../hooks/useConsignorId';
import { ConsignmentContract, ConsignmentOrder } from '../types/consignment.types';

// ─── Reusable sub-components ──────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const classes: Record<string, string> = {
    draft:             'bg-gray-100 text-gray-700',
    pending_signature: 'bg-yellow-100 text-yellow-800',
    active:            'bg-green-100 text-green-800',
    settled:           'bg-blue-100 text-blue-800',
    cancelled:         'bg-red-100 text-red-700',
    receiving:         'bg-orange-100 text-orange-800',
    processing:        'bg-yellow-100 text-yellow-800',
    cataloged:         'bg-sky-100 text-sky-800',
    live:              'bg-green-100 text-green-800',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${classes[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm font-medium text-gray-500 sm:w-44 flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-900">{value ?? <span className="text-gray-400 italic">Not specified</span>}</span>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const ConsignorContractView: React.FC<{ consignorIdOverride?: string; basePath?: string }> = ({
  consignorIdOverride,
  basePath = '/consignor',
}) => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const { consignorId, loading: idLoading } = useConsignorId(consignorIdOverride);

  const [contract, setContract] = useState<(ConsignmentContract & { id: string }) | null>(null);
  const [relatedCOs, setRelatedCOs] = useState<(Partial<ConsignmentOrder> & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!consignorId || !contractId) return;
    loadContract(consignorId, contractId);
  }, [consignorId, contractId]);

  const loadContract = async (cid: string, ctId: string) => {
    try {
      const snap = await getDoc(doc(db, 'consignmentContracts', ctId));
      if (!snap.exists()) {
        toast.error('Contract not found.');
        navigate(`${basePath}/dashboard`);
        return;
      }
      const data = snap.data() as ConsignmentContract;

      // Security: verify this contract belongs to the current user's consignor
      if (data.consignorId !== cid) {
        toast.error('You do not have access to this contract.');
        navigate(`${basePath}/dashboard`);
        return;
      }

      setContract({
        id: snap.id,
        ...data,
        createdAt: (data.createdAt as any)?.toDate?.() ?? data.createdAt,
        contractDate: (data.contractDate as any)?.toDate?.() ?? data.contractDate,
      });

      // Load related COs. The query MUST filter by consignorId so the ownership
      // rule (allow read: uid == resource.data.consignorId) permits it — a
      // contractId-only query would be denied for a consignor. Narrow to this
      // contract client-side (also avoids needing a consignorId+contractId index).
      const coSnap = await getDocs(query(
        collection(db, 'consignmentOrders'),
        where('consignorId', '==', cid),
      ));
      setRelatedCOs(
        coSnap.docs
          .map(d => ({ id: d.id, ...d.data() } as any))
          .filter((co: any) => co.contractId === ctId),
      );
    } catch (err) {
      console.error('Error loading contract:', err);
      toast.error('Failed to load contract.');
    } finally {
      setLoading(false);
    }
  };

  if (idLoading || loading) {
    return (
      <div className="max-w-3xl mx-auto card py-20 text-center text-gray-500">
        <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p>Loading contract…</p>
      </div>
    );
  }

  if (!contract) return null;

  const { consignorInfo, pricing, marketing, unsoldPolicy, settlement, legal, signature } = contract;

  const marketingTotal =
    (marketing?.emailBlasts ?? 0) * (marketing?.emailBlastCost ?? 0) +
    (marketing?.socialMediaVideos ?? 0) * (marketing?.socialMediaCost ?? 0) +
    (marketing?.auctionZipAd ? (marketing?.auctionZipCost ?? 0) : 0) +
    (marketing?.otherCost ?? 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to={`${basePath}/dashboard`} className="hover:text-primary-600 transition-colors">
          My Portal
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Contract {contract.id}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-mono">{contract.id}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {contract.createdAt ? `Created ${format(contract.createdAt as Date, 'MMMM d, yyyy')}` : ''}
            {contract.contractDate ? ` · Signed ${format(contract.contractDate as Date, 'MMMM d, yyyy')}` : ''}
          </p>
        </div>
        <StatusBadge status={contract.status} />
      </div>

      {/* ── Consignor Information ───────────────────────────────────────── */}
      <div className="card space-y-1">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Consignor Information</h2>
        <InfoRow label="Name" value={consignorInfo?.name} />
        <InfoRow label="Address" value={
          consignorInfo
            ? `${consignorInfo.address}, ${consignorInfo.city}, ${consignorInfo.state} ${consignorInfo.zip}`
            : null
        } />
        <InfoRow label="Phone" value={consignorInfo?.phone} />
        <InfoRow label="Email" value={
          consignorInfo?.email
            ? <a href={`mailto:${consignorInfo.email}`} className="text-primary-600 hover:underline">{consignorInfo.email}</a>
            : null
        } />
        {consignorInfo?.taxId && <InfoRow label="Tax ID" value={consignorInfo.taxId} />}
      </div>

      {/* ── Sale Type & Platform ─────────────────────────────────────────── */}
      <div className="card space-y-1">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Sale Type & Platform</h2>
        <InfoRow label="Sale Type" value={
          <span className="capitalize">{contract.saleType?.replace('_', ' ')}</span>
        } />
        <InfoRow label="Platform" value={
          <span className="capitalize">{contract.platform?.replace('_', ' ')}</span>
        } />
      </div>

      {/* ── Pricing & Commission ─────────────────────────────────────────── */}
      <div className="card space-y-1">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Pricing & Commission</h2>
        <InfoRow label="Minimum Bid" value={pricing?.minimumBid != null ? `$${pricing.minimumBid}` : null} />
        <InfoRow label="Commission Rate" value={pricing?.commissionRate != null ? `${pricing.commissionRate}%` : null} />
        {/* §2 is all-inclusive: "No labor charge, no marketing charge and no listing fees
            are billed to the Consignor." Shown ONLY for pre-2026 contracts that carry it. */}
        <InfoRow label="Labor Rate" value={pricing?.laborRate != null ? `$${pricing.laborRate}/hr (pre-2026 contract)` : null} />
        {pricing?.laborHoursEstimate != null && (
          <InfoRow label="Estimated Labor" value={`${pricing.laborHoursEstimate} hrs`} />
        )}

        {/* Additional rates */}
        {(pricing?.additionalRates?.length ?? 0) > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-500 mb-2">Additional Commission Rates</p>
            {pricing!.additionalRates!.map(rate => (
              <div key={rate.rateId} className="flex justify-between py-1 text-sm">
                <span className="text-gray-700">{rate.description}{rate.appliesTo ? ` (${rate.appliesTo})` : ''}</span>
                <span className="font-semibold text-gray-900">{rate.percentage}%</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Marketing Package ────────────────────────────────────────────── */}
      <div className="card">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Marketing Package</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-1.5 border-b border-gray-100">
            <span className="text-gray-600">
              Email Blasts ({marketing?.emailBlasts ?? 0} × ${marketing?.emailBlastCost ?? 0})
            </span>
            <span className="font-medium text-gray-900">
              ${((marketing?.emailBlasts ?? 0) * (marketing?.emailBlastCost ?? 0)).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between py-1.5 border-b border-gray-100">
            <span className="text-gray-600">
              Social Media ({marketing?.socialMediaVideos ?? 0} video{(marketing?.socialMediaVideos ?? 0) !== 1 ? 's' : ''} + boosted ads)
            </span>
            <span className="font-medium text-gray-900">
              ${((marketing?.socialMediaVideos ?? 0) * (marketing?.socialMediaCost ?? 0)).toFixed(2)}
            </span>
          </div>
          {marketing?.auctionZipAd && (
            <div className="flex justify-between py-1.5 border-b border-gray-100">
              <span className="text-gray-600">AuctionZip Ad</span>
              <span className="font-medium text-gray-900">${(marketing?.auctionZipCost ?? 0).toFixed(2)}</span>
            </div>
          )}
          {marketing?.otherMarketing && (
            <div className="flex justify-between py-1.5 border-b border-gray-100">
              <span className="text-gray-600">{marketing.otherMarketing}</span>
              <span className="font-medium text-gray-900">${(marketing?.otherCost ?? 0).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 font-bold text-gray-900">
            <span>Total Marketing</span>
            <span>${marketingTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* ── Unsold Policy ────────────────────────────────────────────────── */}
      <div className="card space-y-1">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Unsold Items Policy</h2>
        {/*
          These strings are what a CONSIGNOR READS about their own property, so they must
          match contract §5 exactly. The previous mapping rendered "Schmidt disposes after
          sale cycles complete" — the one thing §5 states never happens: "Nothing is
          discarded. Property released under (c) is sold in bulk lots, not disposed of."
        */}
        <InfoRow label="Policy" value={
          unsoldPolicy?.option === 'a_return'
            ? `(a) RETURN — you will be notified when your sales cycle is complete, and may collect your unsold property or pay return shipping within ${unsoldPolicy?.returnDeadlineDays ?? 30} days of notice.`
          : unsoldPolicy?.option === 'b_relist'
            ? '(b) RELIST — unsold property runs through additional sale cycles at Schmidt\'s discretion. You may reclaim any item at any time before its final cycle by giving notice.'
          : unsoldPolicy?.option === 'c_release'
            ? '(c) RELEASE — after the sale cycles above, title to remaining unsold property passes to Schmidt, which may sell it for its own account. Nothing is discarded; released property is sold in bulk lots.'
          // NULL IS A REAL ANSWER, not missing data. §5 supplies the consequence, so say it
          // plainly rather than rendering a blank where a legal effect belongs.
          : `No box was marked — under §5, (b) RELIST applies, and Schmidt will give ${unsoldPolicy?.releaseNoticeDays ?? 14} days' written notice before title passes under (c).`
        } />
        {/* A record must never present a default as a choice. */}
        {unsoldPolicy?.source !== 'elected' && (
          <p className="text-xs text-amber-700 mt-1">
            Recorded as the contract's default — not yet confirmed as your election.
          </p>
        )}
      </div>

      {/* ── Payment Terms ────────────────────────────────────────────────── */}
      <div className="card space-y-1">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Payment Terms</h2>
        <InfoRow
          label="Payment Due"
          value={`${settlement?.paymentDueDays ?? 21} days after conclusion of sale`}
        />
        {settlement?.paymentMethod && (
          <InfoRow label="Payment Method" value={<span className="capitalize">{settlement.paymentMethod}</span>} />
        )}
      </div>

      {/* ── Legal Acknowledgements ───────────────────────────────────────── */}
      <div className="card">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Legal Acknowledgements</h2>
        <div className="space-y-1.5 text-sm">
          {[
            { label: 'Consignor cannot bid on own items (PA State Law)', ok: legal?.consignorCannotBid },
            { label: 'Items cannot be removed after listing', ok: legal?.cannotRemoveAfterListing },
            { label: 'Commission applies if removed', ok: legal?.removalCommissionApplies },
            { label: 'Schmidt not liable for loss/theft/damage', ok: legal?.schmidtNotLiableForLoss },
            { label: 'Has authority to consign', ok: legal?.warranties?.hasAuthorityToConsign },
            { label: 'Property is free & clear', ok: legal?.warranties?.propertyFreeClear },
            { label: 'Powered items guaranteed working', ok: legal?.warranties?.poweredItemsWorking },
            { label: 'Good title passes to buyer', ok: legal?.warranties?.goodTitlePasses },
            { label: 'All information is accurate', ok: legal?.warranties?.informationAccurate },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-2">
              {item.ok
                ? <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                : <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
              }
              <span className={item.ok ? 'text-gray-700' : 'text-gray-400'}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Signature ────────────────────────────────────────────────────── */}
      {signature?.consignorSignature && (
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Signature</h2>
          <div className="border rounded-lg p-3 bg-gray-50 inline-block">
            <img
              src={signature.consignorSignature}
              alt="Consignor signature"
              className="max-h-24 max-w-xs object-contain"
            />
          </div>
          {signature.signedAt && (
            <p className="text-xs text-gray-500 mt-2">
              Signed {format((signature.signedAt as any)?.toDate?.() ?? signature.signedAt, 'MMMM d, yyyy h:mm a')}
            </p>
          )}
        </div>
      )}

      {/* ── Related Consignment Orders ───────────────────────────────────── */}
      <div className="card">
        <h2 className="text-lg font-bold text-gray-900 mb-3">
          Consignment Orders ({relatedCOs.length})
        </h2>
        {relatedCOs.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No orders created yet.</p>
        ) : (
          <div className="space-y-2">
            {relatedCOs.map(co => (
              <Link
                key={co.id}
                to={`${basePath}/orders/${co.id}`}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition group"
              >
                <span className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                  {co.id}
                </span>
                <div className="flex items-center gap-2">
                  {co.status && <StatusBadge status={co.status} />}
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Back */}
      <div>
        <Link to={`${basePath}/dashboard`} className="btn-secondary">
          ← Back to Portal
        </Link>
      </div>
    </div>
  );
};

export default ConsignorContractView;
